import * as THREE from 'three';
import { flatCam, orbitCam } from '../scene/context.js';
import { leftPanelDesc, rightPanelDesc } from '../scene/panels.js';
import { eyeVec } from '../scene/helpers.js';
import { getSel } from '../scene/objects.js';
import { S, clamp } from '../state/store.js';
export { leftPanelDesc, panelCorners, rightPanelDesc } from '../scene/panels.js';

/* ═══════════════════════════════════════════════════════════
   CAMERAS
   ═══════════════════════════════════════════════════════════ */
export const orb = {theta:-0.55, phi:1.18, dist:14, target:new THREE.Vector3(0,0,0)};

export function frameSelection(){
  const o = getSel();
  if(!o || !o.node){ frameOrbit(); return; }
  const b = new THREE.Box3().setFromObject(o.node);
  if(b.isEmpty()){ frameOrbit(); return; }
  b.getCenter(orb.target);
  orb.dist = Math.max(.5, b.getSize(new THREE.Vector3()).length()*1.7);
}
export function frameOrbit(){
  const e = eyeVec();
  /* A corner screen reaches a second panel's width back in depth and sits
     off-centre around the hinge, so framing on the first panel alone would cut
     half the piece out of the default view. All three terms fall back to the
     single screen's own framing exactly when there is no corner. */
  const cw = S.cornerPanel ? S.cornerPanel.w : 0;
  const d = Math.max(S.screen.w, S.screen.h, cw);
  orb.target.set(cw ? S.screen.w/4 : 0, 0, Math.min(e.z*0.35, d*0.5) - cw/2);
  orb.dist = Math.max(e.z*1.15, d*2.0);
  orb.theta = -0.62; orb.phi = 1.16;
}
export function updateOrbit(){
  const sp = Math.sin(orb.phi), cp = Math.cos(orb.phi);
  orbitCam.position.set(
    orb.target.x + orb.dist*sp*Math.sin(orb.theta),
    orb.target.y + orb.dist*cp,
    orb.target.z + orb.dist*sp*Math.cos(orb.theta)
  );
  orbitCam.lookAt(orb.target);
  orbitCam.updateMatrixWorld();
}

const _eL = new THREE.Vector3(), _iq = new THREE.Quaternion();

/* Can the second panel be shown from where the viewer stands? Its frustum needs
   the eye to be a real distance off it, and unlike the first panel nothing in
   the solve guarantees that — a viewer nearly in the plane of the second panel
   produces a degenerate frustum and, left alone, a negative far plane and a
   matrix full of NaN. Guarding here converts that into one sentence a caller
   can show, rather than a class of mysteries downstream. */
export function cornerRenderable(){
  const d = rightPanelDesc();
  if(!d) return false;
  return panelEyeDist(d) > 0.05;
}
/** How far the eye stands off a panel, along that panel's own normal. */
export function panelEyeDist(desc){
  return _eL.copy(eyeVec()).sub(desc.pos).applyQuaternion(_iq.copy(desc.quat).invert()).z;
}

/* Off-axis (sheared) frustum — the whole trick lives here. The frustum is
   exactly the panel's own rectangle, on purpose: that's what makes the render
   match what the physical screen can actually show. `margin` widens it
   (panel-centred, so the eye-relative shear is unchanged) without touching
   that — used by On-site's overscan so an object outside the calibrated frame
   still has a grabbable handle. `zoom` narrows it the same way — Flat's own
   live preview aid (S.flatZoom); the export always leaves both at 1, so
   neither ever changes what actually gets rendered for real.

   The eye is expressed in the PANEL's own frame before any of that. For the
   single screen that transform is the identity and the arithmetic is
   unchanged, bit for bit — `sub` of a zero vector is `x - 0`, and an identity
   quaternion reduces `applyQuaternion` to `vx + 0 + 0 - 0`, both exact for
   every finite double. tools/verify.mjs asserts that across 288 states with
   Object.is, so even a -0 flip would be caught. Do not "tidy" the expressions
   below into a form that is merely algebraically equal. */
export function updateScreenCam(cam, desc, margin = 1, zoom = 1){
  const e = eyeVec();
  _eL.copy(e).sub(desc.pos).applyQuaternion(_iq.copy(desc.quat).invert());
  const hw = desc.w/2*margin/zoom, hh = desc.h/2*margin/zoom;
  const near = 0.05, far = _eL.z*4 + 200;
  const k = near/Math.max(1e-4, _eL.z);
  /* Pan slides the same window across the panel. Only while zoomed in and only
     in standalone Flat — at zoom 1 the window IS the panel rect, and moving it
     there would just show background where the deliverable should be. The
     export never passes a zoom, so it never sees a pan either. */
  const px = (zoom > 1 && S.view === 'flat') ? (S.flatPan ? S.flatPan.x : 0) : 0;
  const py = (zoom > 1 && S.view === 'flat') ? (S.flatPan ? S.flatPan.y : 0) : 0;
  cam.position.copy(e);
  cam.quaternion.copy(desc.quat);
  cam.near = near; cam.far = far;
  cam.projectionMatrix.makePerspective(
    (-hw + px - _eL.x)*k, ( hw + px - _eL.x)*k,
    ( hh + py - _eL.y)*k, (-hh + py - _eL.y)*k,
    near, far
  );
  cam.projectionMatrixInverse.copy(cam.projectionMatrix).invert();
  /* Read by scene/gizmo.js to size handles against the sheared frustum. Stamped
     here because this is the only function that can invalidate it, so it cannot
     go stale — and it replaces a `cam === flatCam` name check that already had
     to special-case Split and would have needed another arm per panel. */
  cam.userData.screenDesc = desc;
  cam.updateMatrixWorld();
}

/* ONE CAMERA PER TILE, grown on demand.

   cornerCam is a single named second camera, which was enough while the most
   an installation could have was two panels. An array has as many as it has
   screens, so they come from a pool instead — created lazily, kept for the life
   of the session, and never more than doc.js's MAX_TILES of them.

   Their constructor arguments do not matter: updateScreenCam overwrites
   position, quaternion, near, far and the whole projection matrix on every
   call, which is also why re-using one across tiles would be correct but
   confusing to read at a breakpoint. */
const tileCams = [];
export function tileCam(i){
  while(tileCams.length <= i)
    tileCams.push(new THREE.PerspectiveCamera(45, 1, 0.05, 3000));
  return tileCams[i];
}

export function updateFlatCam(margin = 1, zoom = 1){
  updateScreenCam(flatCam, leftPanelDesc(), margin, zoom);
}
/* Flat's zoom used to live here, as a narrowing of the frustum above — and it
   is now a CSS transform on #fitbox instead (ui/flat-view.js), because a
   narrowed frustum reads as zooming behind a fixed aperture: the box stays put
   and a magnified crop appears inside it, clipped at the edges.

   What stays here is the `zoom` parameter updateScreenCam still accepts, and
   the S.flatPan branch inside it. Both are now unreachable — nothing passes a
   zoom above 1 — and both are left exactly as they were on purpose:
   tools/verify.mjs's section 36 holds that projection bit-exact against every
   export ever delivered, and there is nothing to gain from editing arithmetic
   whose only job is to stay identical. */

/* Split's interactive pane behaves exactly like standalone Orbit — same
   camera, same drag/orbit/select handling — it's only the second, read-only
   pane that renders through flatCam. Everything that used to branch on
   S.view==='orbit' to mean "the interactive camera is orbitCam" needs this
   instead, now that two view values mean that. */
export const isOrbitLike = () => S.view==='orbit' || S.view==='split';
/* AND THE OTHER HALF OF SPLIT. Its right pane used to be the Flat deliverable,
   and the view was offered only without a photograph; with one it holds the
   On-site mockup instead. So "the mockup is on screen" is two view values now,
   exactly as "the interactive camera is orbitCam" already was.

   The strict S.view==='site' test survives only where the STANDALONE view is
   genuinely what is meant — its own full-stage layout, its own zoom, and the
   correction in chrome.js that rescues a site view left without a photo. */
export const isSiteLike = () => S.view==='site' || (S.view==='split' && !!S.photo);
export function activeCam(){ return isOrbitLike() ? orbitCam : flatCam; }
